import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Users,
  Search,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  User,
  CheckCircle,
  Clock,
  MoreHorizontal,
  FileText,
  Archive,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'
import { SimpleIdentity } from '../../model/Identity'
import { Event } from '../../model/Event'
import { isEventActive } from '../../lib/eventDates'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import EventEndpoints from '../../endpoints/EventEndpoints'
import ReportEndpoints from '../../endpoints/ReportEndpoints'
import { IdentityDossierRequest } from '../../requests/report/IdentityDossierRequest'
import { CreateIdentityDialog } from './CreateIdentityDialogModern'

export const IdentityList: React.FC = () => {
  const [identities, setIdentities] = useState<SimpleIdentity[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [clientFilter, setClientFilter] = useState<string>('all')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [statusFilter, setStatusFilter] = useState<'active' | 'all' | 'pending'>('all')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [provisioningEvent, setProvisioningEvent] = useState<Event | null>(null)
  const [loadingEventId, setLoadingEventId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SimpleIdentity | null>(null)
  const [dossierHtml, setDossierHtml] = useState('')
  const [dossierOpen, setDossierOpen] = useState(false)

  const { clients, refreshClients, events, refreshEvents } =
    useApplicationStore()
  const { hasIdentityManagerForAnyone, hasAdmin } = useSessionStore()
  const canCreateIdentities = hasIdentityManagerForAnyone()
  const canBulkImport = hasAdmin()

  const activeClients = clients.filter((c) => !c.dateArchived)

  const upcomingEvents = (events || [])
    .filter((e) => isEventActive(e))
    .sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    )

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, clientFilter, includeArchived, statusFilter])

  // Load clients on mount
  useEffect(() => {
    if (!clients || clients.length === 0) {
      refreshClients().catch(console.error)
    }
  }, [clients, refreshClients])

  // Load events on mount (powers the quick-provision dropdown)
  useEffect(() => {
    if (!events || events.length === 0) {
      refreshEvents().catch(console.error)
    }
  }, [events, refreshEvents])

  // Fetch identities from the API
  const fetchIdentities = useCallback(async () => {
    setLoading(true)
    try {
      if (statusFilter === 'pending') {
        const clientId = clientFilter === 'all' ? undefined : parseInt(clientFilter)
        const results = await IdentityEndpoints.getPendingIdentityList(clientId)
        const filtered = debouncedSearch
          ? results.filter((i) => {
              const term = debouncedSearch.toLowerCase()
              return `${i.firstName} ${i.lastName} ${i.email || ''}`.toLowerCase().includes(term)
            })
          : results
        setIdentities(filtered)
        setTotalCount(filtered.length)
      } else {
        const clientId = clientFilter === 'all' ? undefined : parseInt(clientFilter)
        const includePending = statusFilter === 'all' ? true : undefined
        const [results, countResult] = await Promise.all([
          IdentityEndpoints.getIdentityList(
            clientId,
            undefined,
            undefined,
            pageSize,
            (currentPage - 1) * pageSize,
            debouncedSearch || undefined,
            includeArchived,
            includePending
          ),
          IdentityEndpoints.getIdentityCount(
            clientId,
            debouncedSearch || undefined,
            includeArchived,
            includePending
          ),
        ])
        setIdentities(results)
        setTotalCount(countResult.count)
      }
    } catch (error) {
      console.error('Failed to load identities:', error)
    } finally {
      setLoading(false)
    }
  }, [clientFilter, pageSize, currentPage, debouncedSearch, includeArchived, statusFilter])

  useEffect(() => {
    fetchIdentities()
  }, [fetchIdentities])

  const totalPages = Math.ceil(totalCount / pageSize)
  const startIndex = (currentPage - 1) * pageSize

  const getInitials = (first: string, last: string) => {
    return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase()
  }

  const handlePrintDossier = async (identity: SimpleIdentity) => {
    try {
      const request: IdentityDossierRequest = {
        identityId: identity.id,
        rootUrl: location.origin + '/ems/',
        includeDOBSIN: 'false',
      }
      const res = await ReportEndpoints.getIdentityDossier(request)
      setDossierHtml(res.data)
      setDossierOpen(true)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to generate dossier'))
    }
  }

  const handleArchive = async (identity: SimpleIdentity) => {
    const archiving = !identity.dateArchived
    try {
      await IdentityEndpoints.editIdentity(identity.id, {
        field: 'archived',
        value: archiving ? 'true' : 'false',
      })
      toast.success(
        `${identity.firstName} ${identity.lastName} ${archiving ? 'archived' : 'unarchived'}`
      )
      fetchIdentities()
    } catch {
      toast.error(`Failed to ${archiving ? 'archive' : 'unarchive'} identity`)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await IdentityEndpoints.deleteIdentity(deleteTarget.id)
      toast.success(`${deleteTarget.firstName} ${deleteTarget.lastName} deleted`)
      setDeleteTarget(null)
      fetchIdentities()
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete identity'))
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Identities</h1>
              <p className="text-sm text-muted-foreground">
                Manage people across clients and events
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchIdentities} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canBulkImport && (
            <Button variant="outline" asChild>
              <Link to="/identity/import">
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Link>
            </Button>
          )}
          {canCreateIdentities && (
            <div className="flex">
              <Button
                className="rounded-r-none"
                onClick={() => {
                  setProvisioningEvent(null)
                  setShowCreateDialog(true)
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Identity
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    className="rounded-l-none border-l border-primary-foreground/20"
                    title="Create and provision to event"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="max-h-80 overflow-y-auto w-72"
                >
                  {upcomingEvents.length === 0 ? (
                    <DropdownMenuItem disabled>
                      No upcoming events
                    </DropdownMenuItem>
                  ) : (
                    upcomingEvents.map((event) => (
                      <DropdownMenuItem
                        key={event.id}
                        disabled={loadingEventId !== null}
                        onSelect={async (e) => {
                          e.preventDefault()
                          setLoadingEventId(event.id)
                          try {
                            const full = await EventEndpoints.getEvent(event.id)
                            setProvisioningEvent(full)
                            setShowCreateDialog(true)
                          } catch (err) {
                            toast.error(extractErrorMessage(err, 'Failed to load event'))
                          } finally {
                            setLoadingEventId(null)
                          }
                        }}
                      >
                        <div className="flex flex-col">
                          <span>{event.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {event.client.name}
                          </span>
                        </div>
                        {loadingEventId === event.id && (
                          <Loader2 className="ml-auto h-3 w-3 animate-spin" />
                        )}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {activeClients.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeArchivedIdentities"
                checked={includeArchived}
                onCheckedChange={(checked) => setIncludeArchived(checked === true)}
              />
              <label
                htmlFor="includeArchivedIdentities"
                className="text-sm font-medium leading-none whitespace-nowrap"
              >
                Include Archived
              </label>
            </div>
            {canCreateIdentities && (
              <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="all">All (incl. Pending)</SelectItem>
                  <SelectItem value="pending">Pending Only</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Loading identities...</p>
            </div>
          ) : identities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <User className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-4 text-sm font-medium">No identities found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {debouncedSearch
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating your first identity'}
              </p>
              {!debouncedSearch && canCreateIdentities && (
                <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Identity
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[25%]">Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Regions</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    {canCreateIdentities && <TableHead className="w-[50px]" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {identities.map((identity) => (
                    <TableRow
                      key={identity.id}
                      className={`cursor-pointer transition-colors ${identity.dateArchived ? 'bg-yellow-50/50 hover:bg-yellow-50' : ''}`}
                      onClick={() => window.open(`/identity/${identity.id}`, '_blank', 'noopener')}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {getInitials(identity.firstName, identity.lastName)}
                          </div>
                          <div>
                            <p className="font-medium text-primary">
                              {identity.firstName}
                              {identity.middleName ? ` ${identity.middleName}` : ''}{' '}
                              {identity.lastName}
                            </p>
                            {identity.employeeNumber && (
                              <p className="text-xs text-muted-foreground">#{identity.employeeNumber}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {identity.email || '-'}
                      </TableCell>
                      <TableCell>
                        {identity.parentCustomer ? (
                          <Badge variant="secondary" className="text-xs">
                            {identity.parentCustomer.name}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Global</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {identity.regions && identity.regions.length > 0
                          ? identity.regions.map((r) => r.name).join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {identity.pending ? (
                          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-100">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        ) : identity.dateArchived ? (
                          <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                            Archived
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 hover:bg-green-50">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      {canCreateIdentities && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {identity.pending && (
                                <>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await IdentityEndpoints.approvePendingIdentity(identity.id)
                                        toast.success(`${identity.firstName} ${identity.lastName} approved`)
                                        fetchIdentities()
                                      } catch (error) {
                                        toast.error(extractErrorMessage(error, 'Failed to approve identity'))
                                      }
                                    }}
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem onClick={() => handlePrintDossier(identity)}>
                                <FileText className="mr-2 h-4 w-4" />
                                Print Dossier
                              </DropdownMenuItem>
                              {!identity.pending && (
                                <DropdownMenuItem onClick={() => handleArchive(identity)}>
                                  <Archive className="mr-2 h-4 w-4" />
                                  {identity.dateArchived ? 'Unarchive' : 'Archive'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteTarget(identity)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1) }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {totalCount > 0
                      ? `${startIndex + 1}-${Math.min(startIndex + pageSize, totalCount)} of ${totalCount}`
                      : '0 results'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Identity Dialog */}
      <CreateIdentityDialog
        open={showCreateDialog}
        onOpenChange={(o) => {
          setShowCreateDialog(o)
          if (!o) setProvisioningEvent(null)
        }}
        clients={clients}
        provisioningEvent={provisioningEvent}
        onCreated={(id) => window.open(`/identity/${id}`, '_blank', 'noopener,noreferrer')}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Identity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{' '}
              {deleteTarget && `${deleteTarget.firstName} ${deleteTarget.lastName}`}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dossier Viewer */}
      <Dialog open={dossierOpen} onOpenChange={setDossierOpen}>
        <DialogContent className="sm:max-w-[90vw] w-[90vw] h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Identity Dossier
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            <iframe
              title="Dossier Preview"
              srcDoc={dossierHtml}
              className="w-full h-full border rounded-md"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
